import { __ } from "@wordpress/i18n";
import { createRoot } from "react-dom/client";
import { useCallback, useEffect, useState, useRef } from "react";
import Slider from "react-slick";
import BSHelper from "../BSHelper";
import Constants from "../constants";
import { fetchData } from "../api";
import NoDataMsg from "../components/NoDataMsg.component";
import { Spinner } from "@wordpress/components";

function SliderNextArrow(props) {
	const { className, style, onClick } = props;
	return (
		<button
			type="button"
			onClick={onClick}
			className={`${className} border border-body p-3 rounded-full transition-all hover:bg-primary hover:text-white hover:border-primary group`}
			aria-label="Button"
		>
			<span className="sr-only">{__("Next", Constants.TEXT_DOMAIN)}</span>
			<svg
				className="w-5 h-5 group-hover:fill-white"
				viewBox="0 0 20 20"
				fill="currentColor"
				aria-hidden="true"
			>
				<path
					fillRule="evenodd"
					d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z"
					clipRule="evenodd"
				/>
			</svg>
		</button>
	);
}

function SliderPrevArrow(props) {
	const { className, style, onClick } = props;
	return (
		<button
			type="button"
			onClick={onClick}
			className={`${className} border border-body me-3 p-3 rounded-full transition-all hover:bg-primary hover:text-white hover:border-primary group`}
			aria-label="Button"
		>
			<span className="sr-only">{__("Previous", Constants.TEXT_DOMAIN)}</span>
			<svg
				className="w-5 h-5 ltr:-scale-x-100 group-hover:fill-white"
				viewBox="0 0 20 20"
				fill="currentColor"
				aria-hidden="true"
			>
				<path
					fillRule="evenodd"
					d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z"
					clipRule="evenodd"
				/>
			</svg>
		</button>
	);
}

/**
 * Renders a view component with tabs and content.
 *
 * @return {JSX.Element} The rendered view component.
 */
export default function View() {
	const [itemsList, setItemsList] = useState([]);
	const [itemsCount, setItemsCount] = useState(0);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(null);
	const itemsPerPage = 3;
	const noResultMsg = __("Sorry, no results found.", Constants.TEXT_DOMAIN);
	const sliderSettings = {
		className: "slider",
		dots: true,
		infinite: true,
		centerMode: true,
		slidesToShow: 4,
		slidesToScroll: 1,
		variableWidth: true,
		rows: 1,
		autoplay: true,
		autoplaySpeed: 2000,
		rtl: BSHelper.isPageRTL(),
		responsive: [
		  {
			breakpoint: 1024,
			settings: {
			  slidesToShow: 3,
			  slidesToScroll: 1,
			  infinite: true,
			}
		  },
		  {
			breakpoint: 600,
			settings: {
			  slidesToShow: 2,
			  slidesToScroll: 1,
			}
		  },
		  {
			breakpoint: 480,
			settings: {
			  slidesToShow: 1,
			  slidesToScroll: 1
			}
		  }
		],
		appendDots: (dots) => (
			<div>
				<div className="container flex items-center ltr:flex-row rtl:flex-row-reverse ltr:me-auto rtl:ms-auto rtl:me-0">
					<div className="flex flex-row ltr:pe-8 rtl:ps-8 rtl:pe-0 slider-nav">
						<SliderPrevArrow className="button" onClick={previous} />
						<SliderNextArrow className="button" onClick={next} />
					</div>
					<ul className="flex flex-row w-full"> {dots} </ul>
				</div>
			</div>
		),
	};
	let sliderRef = useRef(null);
	const next = () => {
		sliderRef.slickNext();
	};
	const previous = () => {
		sliderRef.slickPrev();
	};

	const fetchItemsList = useCallback(async (page = 1) => {
		setLoading(true);
		setError(null);
		// console.log(`Fetching sliders for filter ${filter}`);

		try {
			// console.log(`Fetching sliders for page ${page}`);
			const response = await fetchData(
				"/bseitwp/v1/sliders/location/ourmission",
			);
			// console.log("API Response:", response);
			setItemsList(response.data.data);
			setItemsCount(response.data.count);
			if (response.data.count === 0) {
				setError({ message: noResultMsg });
			}
		} catch (error) {
			setError(error);
		} finally {
			setLoading(false);
		}
	}, []);

	// Fetch items list on mount
	useEffect(() => {
		fetchItemsList();
	}, [fetchItemsList]);
	return (
		<>
			<div className="slider-container ms-8 lg:ms-[3vw] xl:ms-[9vw] 2xl:ms-52 mb-14">
				{loading && (
					<div className="flex items-center justify-center w-full min-h-80">
						<Spinner
							style={{
								height: "calc(2px * 20)",
								width: "calc(2px * 20)",
							}}
						/>
					</div>
				)}

				{!loading && (
					<>
						<Slider
							{...sliderSettings}
							ref={(slider) => {
								sliderRef = slider;
							}}
							className="pb-20"
						>
							{itemsCount > 0 &&
								itemsList.map((item, index) => (
									<div
										key={index}
										className="relative"
										data-fancybox="gallery"
										data-src={item.image}
									>
										<img
											className="w-full lg:h-[420px] object-cover rounded-xl"
											src={item.meta_data.slide_image}
											alt={item.title}
											loading="lazy"
										/>
										<div className="absolute inset-0 bg-black opacity-40 rounded-xl"></div>
										<div className="absolute inset-0 flex items-end justify-center pb-5">
											<div className="px-5 text-white text-start">
												<p className="mt-4 text-lg">{item.title}</p>
											</div>
										</div>
									</div>
								))}
						</Slider>
						{error && (
							<div className="flex items-center justify-center w-full min-h-80">
								<NoDataMsg
									showImage={true}
									showTitle={false}
									showMsg={true}
									showButton={false}
									msg={error.message}
									imageSrc="#"
								/>
							</div>
						)}
					</>
				)}
			</div>
		</>
	);
}
const rootElement = document.getElementById("ourMissionSliderRoot");
const root = createRoot(rootElement);

root.render(<View />);
